import { useState, useCallback, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { isFunction, compact } from "lodash"
import { AppBar, Toolbar, Typography, Drawer, IconButton } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { hasPermission, PERMISSION_LEVELS } from "helpers/permissions"
import MetricsSelectorFooter from "./metrics-selector-footer.jsx"
import MetricsSelectorHeader from "./metrics-selector-header"
import { AvailableMetricsShape } from "./helpers/prop-types"
import MetricSelection from "./metric-selection"
import MetricColumnOrdering from "./metric-column-ordering"
import { filterProcessedMetrics, processMetrics } from "./helpers"
import { MetricsTemplateShape } from "./helpers/prop-types"
import "./styles.scss"

export default function MetricSelector({
  isOpen,
  toggle,
  defaultSelectedMetrics,
  availableMetrics,
  includeMetricTypes = false,
  saveCustomTemplate,
  deleteCustomTemplate,
  customTemplate,
  analytics,
}) {
  const [metricsSearchValue, setMetricsSearchValue] = useState("")
  const [localCustomViewName, setLocalCustomViewName] = useState("")
  const [canSaveView, setCanSaveView] = useState(false)
  const [localSelectedMetrics, setLocalSelectedMetrics] = useState(defaultSelectedMetrics)

  const hasDeleteAccess = useMemo(
    () => hasPermission(customTemplate?.permissionLevel, PERMISSION_LEVELS.OWNER),
    [customTemplate],
  )

  useEffect(() => {
    if (customTemplate) {
      setLocalCustomViewName(customTemplate.name)
      setLocalSelectedMetrics(customTemplate.config.metrics)
      setCanSaveView(true)
    } else {
      setLocalCustomViewName("")
      setLocalSelectedMetrics(defaultSelectedMetrics)
      setCanSaveView(false)
    }
  }, [customTemplate, setLocalCustomViewName, setLocalSelectedMetrics, setCanSaveView, defaultSelectedMetrics])

  const saveSelectedMetrics = useCallback(() => {
    toggle()
    saveCustomTemplate({ name: localCustomViewName, metrics: compact(localSelectedMetrics) })
  }, [saveCustomTemplate, localCustomViewName, localSelectedMetrics, toggle])

  const deleteSelectedMetrics = useCallback(() => {
    deleteCustomTemplate(customTemplate)
    toggle()
  }, [deleteCustomTemplate, customTemplate, toggle])

  const processedAvailableMetrics = useMemo(() =>
    processMetrics(availableMetrics, localSelectedMetrics),
  [availableMetrics, localSelectedMetrics],
  )

  const filteredMetrics = useMemo(() =>
    filterProcessedMetrics(processedAvailableMetrics, metricsSearchValue),
  [processedAvailableMetrics, metricsSearchValue],
  )

  return <Drawer
    anchor="right"
    open={isOpen}
    onClose={toggle}
    classes={{
      paper: "components_custom-metrics-selector",
    }}
  >
    <AppBar
      position="sticky"
      classes={{
        root: "metrics-selector-app-bar",
      }}
      sx={{
        boxShadow: "none",
      }}
    >
      <Toolbar className="metrics-selector-toolbar">
        <Typography variant="h6" color="inherit" noWrap>
          Create a Custom View
        </Typography>
        {isFunction(toggle) && <IconButton onClick={toggle} color="inherit">
          <CloseIcon />
        </IconButton>}
      </Toolbar>
    </AppBar>
    <div className="custom-metrics-selector-wrapper">
      <MetricsSelectorHeader
        customViewName={localCustomViewName}
        setCustomViewName={setLocalCustomViewName}
        setCanSaveView={setCanSaveView}
      />
      <div className="metrics-selector-content">
        <div className="left-side">
          <MetricSelection
            shouldFocus={isOpen}
            filteredMetrics={filteredMetrics}
            selectedMetrics={localSelectedMetrics}
            setSelectedMetrics={setLocalSelectedMetrics}
            metricsSearchValue={metricsSearchValue}
            setMetricsSearchValue={setMetricsSearchValue}
            includeMetricTypes={includeMetricTypes}
            analytics={analytics}
          />
        </div>
        <div className="right-side">
          <MetricColumnOrdering
            availableMetrics={processedAvailableMetrics}
            selectedMetrics={localSelectedMetrics}
            setSelectedMetrics={setLocalSelectedMetrics}
            analytics={analytics}
          />
        </div>
      </div>
    </div>
    <MetricsSelectorFooter
      deleteSelectedMetrics={deleteSelectedMetrics}
      saveSelectedMetrics={saveSelectedMetrics}
      canSaveView={canSaveView}
      isEditing={!!customTemplate}
      hasDeleteAccess={hasDeleteAccess}
      analytics={analytics}
    />
  </Drawer>
}

MetricSelector.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  availableMetrics: AvailableMetricsShape,
  saveCustomTemplate: PropTypes.func.isRequired,
  deleteCustomTemplate: PropTypes.func.isRequired,
  defaultSelectedMetrics: PropTypes.objectOf(PropTypes.bool).isRequired,
  includeMetricTypes: PropTypes.bool,
  customTemplate: MetricsTemplateShape,
  analytics: PropTypes.object,
}
